import React, { useEffect, useState, useMemo } from 'react';
import { Theme } from '../types/theme';
import { useSessionState } from '../hooks/useSessionState';
import { APP_CONFIG_DEFAULTS } from '../../src/app-config';
import { ActiveView } from '../types';
import { useToast } from './ToastContext';
import { useVoiceSession } from '../hooks/useVoiceSession';
import FullscreenView from './views/FullscreenView';
import WidgetView from './views/WidgetView';

interface ChatWidgetProps {
    activeView: ActiveView;
    viewMode: 'widget' | 'fullscreen';
    onClose: () => void;
    onMinimize: () => void;
    onFullscreen: (preserveActiveView?: boolean) => void;
    onViewChange: (view: ActiveView) => void;
    theme: Theme;
    agentName?: string;
    onThemeChange: (theme: Theme) => void;
    companyName?: string;
}

const ChatWidget: React.FC<ChatWidgetProps> = ({
    activeView,
    viewMode,
    onClose,
    onViewChange,
    agentName,
    theme,
    onThemeChange,
    companyName
}) => {
    const isWideScreen = typeof window !== 'undefined' && window.innerWidth >= 1280;
    const [showVoiceInSplit, setShowVoiceInSplit] = useState(false);
    const {
        isInitialized,
        hasSession,
        createNewSession,
        clearSession,
        messages,
        addMessage,
        updateMessage,
        userName,
        sessionId,
        setMessages,
        updateIsProfileUpdated,
    } = useSessionState();

    const { showError } = useToast();

    const appConfig = useMemo(() => ({
        backendUrl: APP_CONFIG_DEFAULTS.backendUrl,
        liveKitUrl: APP_CONFIG_DEFAULTS.liveKitUrl,
        sandboxId: APP_CONFIG_DEFAULTS.sandboxId,
        supportsChatInput: APP_CONFIG_DEFAULTS.supportsChatInput ?? true,
        supportsVideoInput: APP_CONFIG_DEFAULTS.supportsVideoInput ?? true,
        supportsScreenShare: APP_CONFIG_DEFAULTS.supportsScreenShare ?? true,
        isPreConnectBufferEnabled: APP_CONFIG_DEFAULTS.isPreConnectBufferEnabled ?? true,
        startButtonText: APP_CONFIG_DEFAULTS.startButtonText ?? 'Start',
    }), []);

    const handleDisconnect = () => {
        setShowVoiceInSplit(false);
    };

    const {
        isVoiceConnecting,
        isVoiceSessionActive,
        isPreparingToken,
        connectionDetails,
        handleDisconnect: handleVoiceDisconnect,
        preflightVoiceCheck
    } = useVoiceSession({
        activeView,
        onViewChange,
        hasSession,
        userName,
        sessionId,
        appConfig,
        onDisconnect: handleDisconnect
    });

    // Welcome message on new session
    useEffect(() => {
        // if (!userName || !sessionId || messages.length > 0) return;
        // const welcome = `Hi ${userName}! I'm ${agentName || 'your Smart Agent'}. How can I help you today?`;
        // addMessage({ type: 'assistant', content: welcome });
    }, [userName, sessionId, messages.length, agentName, addMessage]);

    const handleWelcomeComplete = async (name: string) => {
        try {
            await createNewSession(name);
            onViewChange('chat');
        } catch (error) {
            console.error('Failed to complete welcome:', error);
            showError('Session Error', 'Failed to create session. Please try again.');
        }
    };

    if (viewMode === 'fullscreen' && isWideScreen) {
        return (
            <FullscreenView
                theme={theme}
                onThemeChange={onThemeChange}
                hasSession={hasSession}
                clearSession={clearSession}
                onViewChange={onViewChange}
                agentName={agentName}
                messages={messages}
                addMessage={addMessage}
                updateMessage={updateMessage}
                userName={userName}
                sessionId={sessionId}
                setMessages={setMessages}
                showVoiceInSplit={showVoiceInSplit}
                connectionDetails={connectionDetails}
                appConfig={appConfig}
                isVoiceSessionActive={isVoiceSessionActive}
                handleDisconnect={handleVoiceDisconnect}
                companyName={companyName}
            />
        );
    }

    return (
        <WidgetView
            theme={theme}
            onThemeChange={onThemeChange}
            hasSession={hasSession}
            clearSession={clearSession}
            activeView={activeView}
            onViewChange={onViewChange}
            agentName={agentName}
            messages={messages}
            addMessage={addMessage}
            updateMessage={updateMessage}
            userName={userName}
            sessionId={sessionId}
            setMessages={setMessages}
            connectionDetails={connectionDetails}
            appConfig={appConfig}
            isVoiceSessionActive={isVoiceSessionActive}
            isVoiceConnecting={isVoiceConnecting}
            isPreparingToken={isPreparingToken}
            handleDisconnect={handleVoiceDisconnect}
            onClose={onClose}
            preflightVoiceCheck={preflightVoiceCheck}
            isInitialized={isInitialized}
            onWelcomeComplete={handleWelcomeComplete}
            updateIsProfileUpdated={updateIsProfileUpdated}
            companyName={companyName}
        />
    );
};

export default ChatWidget;